import { Sort } from 'application/shared/sort/sort'

/**
 * Блок «Отзывы участников» на странице квеста.
 *
 * Сервер отдаёт СРАЗУ все отзывы в DOM, а фильтрация по рангу, сортировка
 * и пагинация выполняются на клиенте. Раньше эти три механизма были
 * заглушками (только console.log) — здесь они реализованы.
 *
 * Модель пагинации — постраничный показ: одновременно видна ОДНА страница
 * из `PER_PAGE` карточек. Смена фильтра/сортировки сбрасывает на первую
 * страницу и пересобирает набор и кнопки пагинации.
 */

// Совпадает с серверным дефолтом `@reviews_per_page || 6` в _reviews.html.erb
const PER_PAGE = 6
// Столько номеров страниц показываем вокруг текущей (как max_visible_buttons в вёрстке)
const MAX_VISIBLE_PAGES = 2

export function initReviews() {
  document
    .querySelectorAll<HTMLElement>(
      '[data-quest-reviews], [data-latest-reviews]'
    )
    .forEach(bindShowMore)

  const root = document.querySelector<HTMLElement>('[data-quest-reviews]')
  if (!root) return

  new ReviewsSection(root)
}

class ReviewsSection {
  private root: HTMLElement
  private grid: HTMLElement
  private paginationEl: HTMLElement | null
  private loadMoreBtn: HTMLElement | null
  private filterButtons: HTMLElement[]
  private cards: HTMLElement[]

  // Состояние блока
  private filter = 'all' // 'all' или значение ранга (data-slug = experience)
  private sort = 'newest' // 'newest' | 'earliest'
  private page = 1 // текущая страница, 1-based

  constructor(root: HTMLElement) {
    this.root = root
    this.grid = root.querySelector<HTMLElement>('[data-reviews-grid]')!
    this.paginationEl = root.querySelector<HTMLElement>('[data-pagination]')
    this.loadMoreBtn = root.querySelector<HTMLElement>(
      '[data-action="load-more"]'
    )
    this.filterButtons = Array.from(
      root.querySelectorAll<HTMLElement>('[data-filter]')
    )
    this.cards = Array.from(
      this.grid.querySelectorAll<HTMLElement>('[data-quest-reviews-card]')
    )

    // «Читать далее» — разворот длинного текста
    // Общий обработчик подключён для ленты квеста и хабов в initReviews.
    this.bindFilters()
    this.bindSort()
    this.bindPagination()
    this.bindLoadMore()

    this.render()
  }

  // --- Данные ---

  // Карточки после фильтра по рангу и сортировки по дате.
  private selectedCards(): HTMLElement[] {
    let cards = this.cards
    if (this.filter !== 'all') {
      cards = cards.filter((card) => card.dataset.slug === this.filter)
    }

    const direction = this.sort === 'earliest' ? 1 : -1
    return [...cards].sort(
      (a, b) =>
        direction *
        (Number(a.dataset.createdAt || 0) - Number(b.dataset.createdAt || 0))
    )
  }

  private pagesCount(itemsCount: number): number {
    return Math.max(1, Math.ceil(itemsCount / PER_PAGE))
  }

  // --- Отрисовка ---

  private render() {
    const cards = this.selectedCards()
    const totalPages = this.pagesCount(cards.length)
    this.page = Math.min(this.page, totalPages)

    // Скрываем все карточки (inline display побеждает display:flex карточки
    // по специфичности), затем показываем окно текущей страницы в нужном порядке.
    this.cards.forEach((card) => {
      card.style.display = 'none'
    })

    const start = (this.page - 1) * PER_PAGE
    cards.slice(start, start + PER_PAGE).forEach((card) => {
      card.style.display = ''
      this.grid.appendChild(card) // порядок в DOM = порядок сортировки
    })

    this.renderPagination(totalPages)
    this.renderLoadMore(totalPages)
  }

  // Пересобираем кнопки пагинации под актуальные page/totalPages.
  // Разметка и классы повторяют app/views/ui/_pagination.html.erb,
  // чтобы применялись те же стили.
  private renderPagination(totalPages: number) {
    if (!this.paginationEl) return

    if (totalPages <= 1) {
      this.paginationEl.style.display = 'none'
      this.paginationEl.innerHTML = ''
      return
    }
    this.paginationEl.style.display = ''

    const current = this.page

    let start: number
    if (current <= MAX_VISIBLE_PAGES) start = 1
    else if (current > totalPages - MAX_VISIBLE_PAGES)
      start = totalPages - MAX_VISIBLE_PAGES
    else start = current - Math.floor(MAX_VISIBLE_PAGES / 2)
    start = Math.max(1, start)
    const end = Math.min(start + MAX_VISIBLE_PAGES, totalPages)

    const showLessDots = current > MAX_VISIBLE_PAGES
    const showMoreDots =
      totalPages > MAX_VISIBLE_PAGES + 1 && end !== totalPages

    const arrow =
      '<svg width="14" height="13"><use href="#arrow-left"></use></svg>'
    const button = (attrs: string, inner: string, extraClass = '') =>
      `<button type="button" class="pagination__button ${extraClass}" ${attrs}>${inner}</button>`

    let html = button(
      `data-action="prev"${current === 1 ? ' disabled' : ''}`,
      `<span class="visually-hidden">Предыдущая страница</span>${arrow}`
    )

    if (showLessDots) {
      html += button(
        'data-action="dots-less"',
        '<span class="visually-hidden">К началу списка</span>...'
      )
    }

    for (let n = start; n <= end; n++) {
      html += button(
        `data-action="set-page" data-page="${n}"`,
        String(n),
        n === current ? 'active' : ''
      )
    }

    if (showMoreDots) {
      html += button(
        'data-action="dots-more"',
        '<span class="visually-hidden">К концу списка</span>...'
      )
      html += button(
        `data-action="set-page" data-page="${totalPages}"`,
        String(totalPages)
      )
    }

    html += button(
      `data-action="next"${current === totalPages ? ' disabled' : ''}`,
      `<span class="visually-hidden">Следующая страница</span>${arrow}`,
      'pagination__button_next'
    )

    this.paginationEl.innerHTML = html
  }

  private renderLoadMore(totalPages: number) {
    if (!this.loadMoreBtn) return
    // «загрузить ещё» = перейти на следующую страницу; на последней прячем
    this.loadMoreBtn.style.display = this.page >= totalPages ? 'none' : ''
  }

  private goToPage(page: number) {
    const totalPages = this.pagesCount(this.selectedCards().length)
    this.page = Math.min(Math.max(1, page), totalPages)
    this.render()
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // --- Обработчики ---

  private bindFilters() {
    this.filterButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.filterButtons
          .find((b) => b.classList.contains('active'))
          ?.classList.remove('active')
        btn.classList.add('active')

        this.filter = btn.dataset.filter || 'all'
        this.page = 1 // при смене фильтра всегда возвращаемся на первую страницу
        this.render()
      })
    })
  }

  private bindSort() {
    const sortEl = this.root.querySelector<HTMLElement>('[data-reviews-sort]')
    if (!sortEl) return

    new Sort({
      element: sortEl,
      value: sortEl.getAttribute('data-default-value') || 'newest',
      onChange: (value: string) => {
        this.sort = value || 'newest'
        this.page = 1
        this.render()
      }
    })
  }

  private bindPagination() {
    this.paginationEl?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement

      if (target.closest('[data-action="prev"]')) {
        this.goToPage(this.page - 1)
      } else if (target.closest('[data-action="next"]')) {
        this.goToPage(this.page + 1)
      } else if (target.closest('[data-action="dots-less"]')) {
        this.goToPage(this.page - MAX_VISIBLE_PAGES)
      } else if (target.closest('[data-action="dots-more"]')) {
        this.goToPage(this.page + MAX_VISIBLE_PAGES)
      } else {
        const pageBtn = target.closest<HTMLElement>('[data-page]')
        if (pageBtn) this.goToPage(Number(pageBtn.dataset.page))
      }
    })
  }

  private bindLoadMore() {
    this.loadMoreBtn?.addEventListener('click', () =>
      this.goToPage(this.page + 1)
    )
  }
}

/** «Читать далее»: разворачиваем обрезанный текст отзыва (как было раньше).
 * @param {HTMLElement} root Контейнер ленты отзывов.
 */
function bindShowMore(root: HTMLElement) {
  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    const moreBtn = target.closest<HTMLElement>('[data-show-button]')
    if (!moreBtn) return

    const card = moreBtn.closest<HTMLElement>('[data-quest-reviews-card]')
    const fullContent = card?.querySelector<HTMLElement>('[data-full-content]')
    const shortContent = card?.querySelector<HTMLElement>(
      '[data-short-content]'
    )
    if (!fullContent || !shortContent) return

    // trim() срезал бы пробел ровно в точке разреза: отзыв режется по 180 символам,
    // и если там был пробел, после раскрытия соседние слова слипались.
    // Убираем только отступы шаблона по краям, не трогая границу фрагментов.
    const head = shortContent
      .textContent!.replace(/^\s+/, '')
      .replace(/\.{3}\s*$/, '')
    const tail = (fullContent.textContent ?? '').replace(/\s+$/, '')

    shortContent.textContent = head + tail

    fullContent.remove()
    moreBtn.remove()
  })
}
